<template>
  <div class="wrapper" v-loading="loading">
    <template v-if="loggedIn">
      <router-link :to="{ name: 'Home' }">
          <div class="logo">
            <img src="/assets/img/light/homelogo.png" alt="" />
          </div>
        </router-link>
        <div class="logged">
          <p>{{ $t('other.views.other.loggedAccount') }}</p>
          <div class="account">
            <img :src="accountLogo" alt="">
            <span>{{ $store.getters.userInfo.username }}</span>
          </div>
          <p class="tip">{{ $t('other.views.other.auth.openAgentTip') }}</p>
          <div class="btn" :class="{ disabled: sidLoading }" @click="onAccessAgent">
            <template v-if="sidLoading">
              <el-icon class="is-loading"><loading-icon /></el-icon>
              {{ $t('other.views.other.auth.loading') }}
            </template>
            <template v-else>
              {{ $t('other.views.other.auth.accessAgent', [appName]) }}
            </template>
          </div>
          <p class="switch">{{ $t('other.views.other.isYourAccount') }}<span @click="switchAccount">{{ $t('other.views.other.switchAccount') }}</span></p>
        </div>
    </template>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { generateSid, checkLogin } from '@/services/auth'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { ref, computed } from 'vue'
import { isDark } from 'app/style'
import emitter from '@/app/emitter.js'
import { IS_QIHOO_HOST } from '@/utils'
import { INTRANET_LOGIN_URL, QIHOO_LOGIN_URL } from '@/constants'
import { Loading as LoadingIcon } from '@element-plus/icons-vue'

const AUTH_QUERY_KEY = 'auth_query'

const { t } = useI18n()
const store = useStore()
const route = useRoute()
const loading = ref(true)
const loggedIn = ref(false)
const sidLoading = ref(false)
const isQihoo = IS_QIHOO_HOST

// 登录成功回跳后，从 localStorage 恢复查询参数
let restoredQuery = null
const savedQuery = localStorage.getItem(AUTH_QUERY_KEY)
if (savedQuery) {
  try {
    restoredQuery = JSON.parse(savedQuery)
  } catch {}
  localStorage.removeItem(AUTH_QUERY_KEY)
}

// 从 URL 或 localStorage 获取参数（localStorage 优先，因为回跳后 URL 可能丢失查询参数）
const appParam = computed(() => (restoredQuery?.app || route.query.app) || 'ata-agent')
const refParam = computed(() => (restoredQuery?.ref || route.query.ref) || '')
const appName = computed(() => {
  if (appParam.value === 'ata-agent') {
    return t('other.views.other.auth.agentName')
  }
  return appParam.value
})

// 跳转到登录页：把查询参数存到 localStorage，ref 只传干净的 origin+pathname（不带 ?&）
const redirectToLogin = () => {
  const query = { app: route.query.app, ref: route.query.ref, t: route.query.t }
  localStorage.setItem(AUTH_QUERY_KEY, JSON.stringify(query))
  const cleanUrl = window.location.origin + window.location.pathname
  if (isQihoo) {
    window.location.href = INTRANET_LOGIN_URL + cleanUrl
  } else {
    window.location.href = QIHOO_LOGIN_URL + cleanUrl
  }
}

// 初始化：检查登录状态，未登录则直接跳转到对应登录页
const init = () => {
  checkLogin().then((res) => {
    if (!res.is_login) {
      redirectToLogin()
    } else {
      loading.value = false
      loggedIn.value = true
    }
  })
}
init()

const accountLogo = computed(() => {
  if (store.getters.userInfo.user_pic && store.getters.userInfo.user_pic.type && store.getters.userInfo.user_pic.base64) {
    return `data:image/${store.getters.userInfo.user_pic.type};base64,${store.getters.userInfo.user_pic.base64}`
  }
  return isDark() ? require('@/assets/img/avatar.png') : require('@/assets/img/avatar-light.png')
})

// 监听切换账号事件
emitter.on('showLogin', () => {
  redirectToLogin()
})

// 点击「访问 360沙箱云智能体」按钮
const onAccessAgent = () => {
  if (sidLoading.value) return
  sidLoading.value = true

  const data = {
    app: appParam.value,
    ref: refParam.value
  }

  generateSid(data).then((res) => {
    const sid = typeof res === 'object' ? (res.sid || res.data || res) : res
    if (sid) {
      const redirectUrl = refParam.value
        ? `${refParam.value}${refParam.value.includes('?') ? '&' : '?'}sid=${sid}`
        : '/'
      window.location.href = redirectUrl
    }
  }).catch(() => {
    sidLoading.value = false
  })
}

const switchAccount = () => {
  emitter.emit('showLogin')
}
</script>

<style lang="scss" scoped>
.wrapper {
  .logo {
    position: absolute;
    top: 10px;
    left: 16px;
    img {
      height: 28px;
    }
  }
  padding: 10px 16px;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  .logged {
    margin-top: -100px;
    p:nth-child(1) {
      font-size: 16px;
      color: #3D3D3D;
    }
    .account {
      background: #F7F7F7;
      border-radius: 5px;
      padding: 10px 16px;
      min-width: 260px;
      display: flex;
      align-items: center;
      span {
        font-size: 14px;
        color: #3D3D3D;
      }
      img {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        margin-right: 16px;
      }
    }
    .tip {
      font-size: 12px;
      color: #3D3D3D;
    }
    .btn {
      background: linear-gradient(270deg, #498DE2 0%, #41A3DE 100%);
      box-shadow: 2px 2px 10px 0px rgba(0, 0, 0, 0.05);
      border-radius: 5px;
      font-size: 14px;
      font-weight: bold;
      color: #FFFFFF;
      text-align: center;
      height: 35px;
      line-height: 35px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      &.disabled {
        opacity: 0.7;
        cursor: not-allowed;
      }
      .el-icon {
        margin-right: 6px;
      }
    }
    .switch {
      font-size: 12px;
      color: #3D3D3D;
      span {
        color: #5264FF;
        cursor: pointer;
      }
    }
  }
}
</style>
